import { useCallback, useEffect, useRef, useState } from 'react';
import { UploadFileEntriesDragDropType } from './UploadFileTypes';
import { UploadFileEntryDragDrop } from './UploadFileEntryDragDrop';
import React from 'react';
import { UploadFileEntriesDragPlaceholder } from './UploadFileEntriesDragPlaceholder';

const UploadFileEntriesDragDrop = ({
	files,
	fileErrors,
	updateFilesCallback,
	removeFileCallback,
	duplicateCallback,
	maxSize,
	extensionDisplayValue,
}: UploadFileEntriesDragDropType) => {
	const [dragIndex, setDragIndex] = useState<number | null>(null);

	const entryHeight = window.getComputedStyle(document.body).getPropertyValue('--lg-avatar-size');

	const ReorderFiles = useCallback(
		(startIndex: number, endIndex: number) => {
			const newFiles = [...files];
			const draggedItem = newFiles[startIndex];

			newFiles.splice(startIndex, 1);

			if (startIndex < endIndex) {
				newFiles.splice(endIndex - 1, 0, draggedItem);
			} else {
				newFiles.splice(endIndex, 0, draggedItem);
			}

			return newFiles;
		},
		[files]
	);

	const DragCleanup = () => {
		document
			.querySelectorAll('.file-drag-placeholder')
			?.forEach((item) => ((item as HTMLDivElement).style.height = ''));

		setDragIndex(null);
	};

	const DropEntry = (e: React.DragEvent) => {
		if (dragIndex !== null) {
			e.preventDefault();
			const dropIndex = parseInt(e.currentTarget.getAttribute('data-order-index') as string, 10);
			DragCleanup();

			if (!Number.isNaN(dropIndex)) {
				const newOrder = ReorderFiles(dragIndex, dropIndex);
				updateFilesCallback(newOrder);
			}
		}
	};

	const DragStart = (e: React.DragEvent, thisDragIndex: number) => {
		e.dataTransfer.setDragImage(new Image(), 0, 0);
		setTimeout(() => {
			setDragIndex(thisDragIndex);
		}, 0);
	};

	const DragEnd = (e: React.DragEvent) => {
		DragCleanup();
	};

	const DragOver = (e: React.DragEvent) => {
		const currentTarget = e.currentTarget as HTMLDivElement;

		const rect = currentTarget?.getBoundingClientRect();
		const inTop = e.clientY - rect?.top <= rect?.height / 2;

		const nextEntry = currentTarget?.nextElementSibling;
		const prevEntry = currentTarget?.previousElementSibling;

		document
			.querySelectorAll('.file-drag-placeholder')
			?.forEach((item) => ((item as HTMLDivElement).style.height = ''));

		if (prevEntry) {
			(prevEntry as HTMLDivElement).style.height = inTop ? entryHeight : '';
		}

		if (nextEntry) {
			(nextEntry as HTMLDivElement).style.height = inTop ? '' : entryHeight;
		}
	};

	return (
		<div
			className={`upload-file-info ${dragIndex !== null ? 'upload-drag-drop-active' : ''}`}
			style={{
				height: dragIndex !== null ? `${parseInt(entryHeight.replace('px', ''), 10) * files.length}px` : 'auto',
			}}
		>
			<div
				data-order-index={0}
				className={`file-drag-placeholder`}
				onDrop={(e) => DropEntry(e)}
				onDragOver={(e) => e.preventDefault()}
			/>

			{files.map((file, index) => (
				<React.Fragment key={`fileUploadDnD${file.size}${file.name}${file.lastModified}`}>
					<div
						data-order-index={index}
						className={`file-drag-wrapper ${index === files.length - 1 ? 'upload-file-entry-last' : ''} ${
							dragIndex !== null && dragIndex === index ? 'drag-active' : ''
						}`}
						draggable
						onDragStart={(e) => DragStart(e, index)}
						onDragEnd={(e) => DragEnd(e)}
						onDragOver={(e) => DragOver(e)}
						onDrop={(e) => DropEntry(e)}
					>
						<UploadFileEntryDragDrop
							file={file}
							valid={!fileErrors.fileNames.includes(file.name) && file.size < maxSize}
							fileType={extensionDisplayValue}
							isDragging={dragIndex !== null}
							removeCallback={() => removeFileCallback(index)}
							duplicateCallback={() => duplicateCallback(index)}
						/>
					</div>
					<div
						data-order-index={index + 1}
						className={`file-drag-placeholder`}
						onDrop={(e) => DropEntry(e)}
						onDragOver={(e) => e.preventDefault()}
					/>
				</React.Fragment>
			))}

			<UploadFileEntriesDragPlaceholder
				dragActive={dragIndex !== null}
				file={files[dragIndex || 0]}
				valid={
					!fileErrors.fileNames.includes(files[dragIndex || 0].name) && files[dragIndex || 0].size < maxSize
				}
				fileType={extensionDisplayValue}
				removeCallback={() => null}
				duplicateCallback={() => null}
				itemHeight={entryHeight}
			/>
		</div>
	);
};

export { UploadFileEntriesDragDrop };
